/**
 * @Author: Colin Osterhout <ctosterhout>
 * @Date:   2018-04-24T19:26:20-08:00
 * @Email:  ctosterhout@alaska.edu
 * @Last modified by:   ctosterhout
 * @Last modified time: 2020-02-17T22:13:04-09:00
 * @License: Released under MIT License. copyright 2019 University of Alaska Southeast.  For more details, see https://opensource.org/licenses/MIT
 */

// ----------------------------------------------------------------------------
// Frameworks
// ----------------------------------------------------------------------------
import { ErnieCore, Vue } from 'ernie-core'
import $ from 'jquery'
import _ from 'lodash'

// ----------------------------------------------------------------------------
// Globally registered components
// ----------------------------------------------------------------------------
import BootstrapVue from 'bootstrap-vue'

// Custom Components
import PageFooter from '../components/PageFooter'
import PageHeader from '../components/PageHeader'
import RecentArticles from '../components/RecentArticles'
import BootstrapAccordionItem from 'ernie-core/components/BootstrapAccordionItem'
import BootstrapAccordion from 'ernie-core/components/BootstrapAccordion'
import ImageGallery from 'ernie-core/components/ImageGallery'
import PageScroller from '../components/PageScroller'
import VueLazyLoad from 'vue-lazyload'

// ----------------------------------------------------------------------------
// Module configuration
// ----------------------------------------------------------------------------
const commonFilters = ErnieCore.commonFilters

const commonPlugins = [
  BootstrapVue,
  VueLazyLoad
]

const commonComponents = {
  BootstrapAccordion: BootstrapAccordion,
  BootstrapAccordionItem: BootstrapAccordionItem,
  ImageGallery: ImageGallery,
  RecentArticles: RecentArticles
}

// ----------------------------------------------------------------------------
// function: initHeader
// parameter: navItems, JSON object containing a collection of menu items to pass
//            to the PageHeader component
// returns: Promise (resolves after PageHeader mounts)
//
// Create the PageHeader component and have it take the place of the element with ID of
// #pageHeader as long as it's present
// ----------------------------------------------------------------------------
const initHeader = (navItems) => {
  return $('#pageHeader').length > 0
    ? new Promise((resolve) => {
      /* eslint-disable no-new */
      new Vue({
        data: {
          navItems: navItems
        },
        el: '#pageHeader',
        template: '<PageHeader />',
        components: {
          PageHeader
        },
        mounted: function () {
          resolve()
        }
      })
      /* eslint-enable no-new */
    })
    : Promise.resolve()
}

// ----------------------------------------------------------------------------
// function: initFooter
// returns: Promise (resolves after footer and footer notice, if required, resolves)
//
// Performs one task:
//  1. Create the PageFooter component to take the place of the existing <footer> w/ id of
//       #pageFooter
// ----------------------------------------------------------------------------
const initFooter = () => {
  return new Promise((resolve) => {
    // Create the page footer
    /* eslint-disable no-new */
    new Vue({
      el: '#pageFooter',
      template: '<PageFooter />',
      components: {
        PageFooter
      },
      mounted: function () {
        resolve()
      }
    })
    /* eslint-enable no-new */
  })
}

// ----------------------------------------------------------------------------
// function: initScroll
// returns: Promise (resolves after component mounted)
//
// Create the PageScroller component which will prompt users to move down the page
// as well as provide a mechanism to return to the top of the page.
// ----------------------------------------------------------------------------
const initScroll = (options = {}) => {
  const $main = $('main').eq(0)
  const $footer = $('footer').eq(0)
  const disableScrollDown = _.isUndefined(options.disableScrollDown)
    ? false
    : options.disableScrollDown

  return new Promise((resolve) => {
    // Create the PageScroller component but don't mount it quite yet
    const vm = new Vue({
      template: `<PageScroller :disable-scroll-down="${disableScrollDown}"/>`,
      components: {
        PageScroller
      },
      mounted: function () {
        resolve()
      }
    })

    // Mount the component but don't attach it to the DOM yet - and spit back out the element
    const componentEl = vm.$mount().$el

    // Mount the component either at the end of the first main section or just above the footer
    if ($main.length) {
      $main.append(componentEl)
    } else if ($footer.length) {
      $footer.insertBefore(componentEl)
    }
  })
}

const AcrcCommon = {
  install: function (Vue, options = {}) {
    const plugins = _.flatten([
      options.plugins || [],
      commonPlugins
    ])
    const components = _.merge({},
      commonComponents,
      options.components
    )
    const filters = _.merge({},
      commonFilters,
      options.filters
    )
    Vue.use(ErnieCore, {
      plugins: plugins,
      components: components,
      filters: filters,
      domains: [
        'acrc.alaska.edu',
        'www.acrc.alaska.edu'
      ]
    })
  },
  render: function (options = {}) {
    return ErnieCore.waitForDOMReady()
      .then(() => {
        return Promise.all([initHeader(), initFooter(), initScroll(_.pick(options, ['disableScrollDown']))])
      })
      .then(ErnieCore.renderTemplates)
      .then(ErnieCore.postRenderProcessing)
  }
}

export {
  AcrcCommon,
  Vue
}
